import { Box } from '@withjoy/joykit';
import React, { useEffect } from 'react';
import { NotificationToast } from '../../../NotificationToast';
import { ProductDetails } from '../../components/ProductDetails';
import { useCatalogProductDetails } from './CatalogProductDetails.controller';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useTranslation } from '@shared/core';
import { Helmet } from 'react-helmet-async';
import {
  useCatalogProductDetailsHealth,
  wrapWithCatalogProductDetailsHealthProvider
} from '@apps/registry/common/components/Catalog/routes/CatalogProductDetails/CatalogProductDetails.health';
import { Seo } from '@shared/components/Seo';
import { getMetaDescription } from '../../utils/metaDescription';

export const CatalogProductDetails = wrapWithCatalogProductDetailsHealthProvider((props: { catalogItemId: string }) => {
  const {
    addCatalogItemToRegistry,
    addCatalogItemLoading,
    hasError,
    error,
    notificationText,
    notificationVisible,
    product,
    setNotificationVisible,
    getPdpDataByProductId,
    shopProductConfirmationToastV2Enabled
  } = useCatalogProductDetails(props);

  const isMobileOrTablet = useIsMobileOrTablet();

  const { t } = useTranslation('curationsRegistry');
  const curationsTranslations = t('curations');
  const defaultDepartmentTitle = curationsTranslations.defaultDepartmentTitle();

  const { onLoadComplete, onLoadFailure } = useCatalogProductDetailsHealth();
  useEffect(() => {
    if (error) {
      onLoadFailure('GetCatalogProductDetailsQuery failed', error);
    } else if (product) {
      onLoadComplete();
    }
  }, [error, onLoadFailure, product, onLoadComplete]);

  const handleHover = (pid?: string, isHoverExit: boolean = false) => {
    const id = isHoverExit ? props.catalogItemId : pid;
    if (id) {
      getPdpDataByProductId({
        variables: {
          id,
          withPdpSections: true,
          withProductRating: true
        }
      });
    }
  };

  const metaDescription = getMetaDescription(
    product?.shortDescription?.trim() ||
      product?.longDescription?.trim() ||
      product?.pdpSections?.find(section => section.sectionType === 'details')?.sectionTree?.elements[2].text,
    product?.title,
    product?.brand?.name
  );

  return (
    <Box
      paddingX={['24px', null, null, null, null, '88px']}
      paddingBottom={`${180 / 16}rem`}
      paddingTop={isMobileOrTablet ? 8 : 0}
      margin="auto"
      marginTop={[0, null, 5]}
      maxWidth={'1568px'}
    >
      {product && (
        <>
          <Seo title={`${product.brand?.name} ${product.title} | Joy`} description={metaDescription} />
          <Helmet>
            {product.title && <meta property="og:title" content={product.title} />}
            {!!product.photos[0]?.url && <meta property="og:image" content={product.photos[0].url} />}
          </Helmet>
        </>
      )}
      <ProductDetails
        key={product?.id}
        showSkeleton={!product || !!hasError}
        product={product}
        addCatalogItem={addCatalogItemToRegistry}
        addCatalogItemLoading={addCatalogItemLoading}
        defaultDepartmentTitle={defaultDepartmentTitle}
        isDialog={false}
        showFooter={true}
        handleHover={handleHover}
      />
      {!shopProductConfirmationToastV2Enabled && (
        <NotificationToast notificationText={notificationText} notificationVisible={notificationVisible} setNotificationVisible={setNotificationVisible} />
      )}
    </Box>
  );
});

CatalogProductDetails.displayName = 'CatalogProductDetails';
